import { useState } from 'react'

import type { ImprovementProposal } from '../../../app/ai-os'
import {
  didSomething,
  mintOperationKey,
  type AiOsOpOutcome,
  type OperationKey,
} from '../../../app/ai-os-operation'
import { blockedByRed, canRetryNow, isRetryable } from '../../../app/module-evolution'
import { Button } from '../../../components/button'
import { T } from '../../../copy/text'

/**
 * 「再来一次」这一块，以及它前面那道红级的门。
 *
 * # 门在界面这一侧也要有一道
 *
 * 服务端有 409 `red_needs_approval`。只有那一道的话，用户会点一下、等一会儿、
 * 然后收到一句他本来就该提前知道的拒绝。所以这里先判 {@link blockedByRed}：
 * **没批过就不给那个按钮**，给的是一条**点得过去**的路。
 *
 * `ai_os.retry.blocked_by_red`（「这件事碰到钱了，重试也得你先点一下」）
 * 单独出现时只是一堵墙——契约为此专门加了 `approval.action_path`，
 * 说的就是这件事：**只说「要人点」而指不出点哪儿，用户读得懂却过不去。**
 * 所以那句话后面永远跟着一个链接。
 *
 * # 幂等键的寿命不在这个组件里
 *
 * 它在 `app/ai-os-operation.ts`。这里只做一件事：每次拿到结局，
 * 用 `key.next(outcome)` 换到下一把。**失败过的键会被换掉**，
 * 而没拿到响应的那种会被留着——两条方向相反，都不能少。
 */

export interface ProposalRetryProps {
  proposal: ImprovementProposal
  /** 发一次重试。键与 expected_version 都由这一层给。 */
  onRetry: (vars: { idempotencyKey: string; expectedVersion: number; actionId?: string }) => void
  pending: boolean
  /** 上一次的结局。`undefined` = 还没发过。 */
  outcome?: AiOsOpOutcome
  /** 后端按契约说的那句话（`user_message`）。它比这边任何一条泛用模板都准。 */
  serverMessage?: string
}

export function ProposalRetry({
  proposal,
  onRetry,
  pending,
  outcome,
  serverMessage,
}: ProposalRetryProps) {
  /**
   * 这一把键。
   *
   * **拿结局去换下一把**，而不是每次点击现生成：
   * 现生成的话，一次没拿到响应的重试会用两把不同的键打两次，
   * 而服务端可能已经执行过第一次了。
   */
  const [key, setKey] = useState<OperationKey>(() => mintOperationKey())
  const [seen, setSeen] = useState<AiOsOpOutcome | undefined>(undefined)

  /**
   * 结局变了就换键。
   *
   * **这一步在组件自己的渲染体里做，不由子组件回调进来。**
   * 第一版把它放在 `RetryOutcome` 里，靠一个 `onSettled` 打回父组件——
   * React 当场警告「在渲染另一个组件时更新了这个组件」，
   * 而那不是风格问题：那种更新可能被丢掉，于是键不换，
   * 下一次重试又拿着那把用掉的键去打。**用例全绿，警告在 stderr 里。**
   *
   * 同一个组件里的「渲染期派生 state」是 React 明确支持的写法
   * （setState 会立刻重跑这次渲染，不提交中间结果）。
   */
  if (outcome && outcome !== seen) {
    setSeen(outcome)
    setKey(key.next(outcome))
  }

  /**
   * **不是所有状态都该看到这一块。** 还在跑的那几档不许重试
   * （契约：对一件还在跑的活再发一次重试，等于同一件事跑两遍）。
   *
   * 但 `outcome` 在的时候要留着——**否则那句「这次成了」会被它自己报的
   * 那次成功干掉**：重试成功之后提案从 `failed` 走到 `executing`，
   * 列表一重取，这一块就整个卸载，用户什么都没看见。
   * （路由级用例逮到的，那条用例点完之后找不到那句话。）
   */
  if (!isRetryable(proposal.state) && !outcome) return null

  const blocked = blockedByRed(proposal)
  const approval = proposal.approval

  return (
    <div
      data-testid="ai-os-retry"
      className="mt-npc-gap-sm flex flex-col gap-npc-gap-xs rounded-npc-inset border border-npc-border-soft bg-npc-panel px-npc-grid-sm py-npc-gap-sm"
    >
      {/* 已经自己试过几次了。0 次不说——「我自己再试一次，第 0 次」不是人话 */}
      {typeof proposal.retry_count === 'number' && proposal.retry_count > 0 ? (
        <p data-testid="ai-os-retry-count" className="m-0 text-npc-meta text-npc-ink-softer">
          <T id="ai_os.retry.auto" vars={{ attempt: proposal.retry_count }} />
        </p>
      ) : null}

      {!isRetryable(proposal.state) ? null : blocked ? (
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          <span data-testid="ai-os-blocked-by-red" className="text-npc-caption text-npc-amber-ink">
            <T id="ai_os.retry.blocked_by_red" />
          </span>
          {/* **指得出点哪儿**。`action_path` 由服务端给，前端不自己拼——
              拼出来的路径在改版那天会静静指向一个 404 */}
          {approval?.action_path ? (
            <a
              data-testid="ai-os-approve-link"
              href={approval.action_path}
              className="text-npc-caption text-npc-blue-ink underline"
            >
              <T id="ai_os.action.approve_red" />
            </a>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          <Button
            size="sm"
            data-testid="ai-os-retry-button"
            disabled={pending || !canRetryNow(proposal) || proposal.version === undefined}
            onClick={() => {
              // `expected_version` 是**我们读到的那个版本**。读不到就不发——
              // 契约里它必填，而这一层没有「不填就强写」这一档
              if (proposal.version === undefined) return
              onRetry({
                idempotencyKey: key.key,
                expectedVersion: proposal.version,
                ...(approval?.action_id ? { actionId: approval.action_id } : {}),
              })
            }}
          >
            <T id="ai_os.action.retry" />
          </Button>
          {pending ? (
            <span data-testid="ai-os-retry-pending" className="text-npc-caption text-npc-ink-soft">
              <T id="ai_os.loading.submitting" />
            </span>
          ) : null}
        </div>
      )}

      <RetryOutcome outcome={outcome} serverMessage={serverMessage} />
    </div>
  )
}

/**
 * 上一次结局说哪一句。
 *
 * 七档各说各的，**因为下一步不一样**：
 *
 *   成了、真的动了      「这次成了」
 *   成了、是重放        **「我已经记过一次了，没有重复记」**——
 *                       这一档最要紧：它长得和成功一模一样（都是 200），
 *                       而实际上什么都没发生。不分开的话，
 *                       用户会以为自己重试过了
 *   还在跑              等一会儿再问，**别并发再跑一遍**
 *   上一次失败了        那个键是终态。下一次会换一把新的（这一层自动做）
 *   版本对不上          去重取一份再来
 *   这一档不能重试
 *   同键不同载荷
 *
 * 后四档服务端按契约给了话就用它那句——它比这边任何一条泛用模板都准
 * （`app/errors.ts` 那条规矩）。
 */
function RetryOutcome({
  outcome,
  serverMessage,
}: {
  outcome?: AiOsOpOutcome
  serverMessage?: string
}) {
  if (!outcome) return null

  if (outcome.kind === 'ok') {
    return didSomething(outcome) ? (
      <p data-testid="ai-os-retry-ok" className="m-0 text-npc-caption text-npc-green-deep">
        <T id="ai_os.retry.manual_ok" />
      </p>
    ) : (
      <p data-testid="ai-os-retry-replayed" className="m-0 text-npc-caption text-npc-ink-soft">
        <T id="ai_os.conflict.already_submitted" />
      </p>
    )
  }

  const testId =
    outcome.kind === 'in_flight'
      ? 'ai-os-retry-in-flight'
      : outcome.kind === 'already_failed'
        ? 'ai-os-retry-already-failed'
        : outcome.kind === 'version_conflict'
          ? 'ai-os-retry-version-conflict'
          : outcome.kind === 'idempotency_mismatch'
            ? 'ai-os-retry-mismatch'
            : outcome.kind === 'red_needs_approval'
              ? 'ai-os-retry-needs-approval'
              : outcome.kind === 'not_retryable'
                ? 'ai-os-retry-not-retryable'
                : 'ai-os-retry-error'

  return (
    <p
      data-testid={testId}
      className="m-0 rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-gap-sm py-npc-gap-xs text-npc-caption text-npc-amber-ink"
    >
      {/* 这几档没有各自的模板，而服务端按契约都会给一句 `user_message`。
          照原样用它，别在前端再包装一次——那就是两份真相 */}
      {serverMessage ? (
        serverMessage
      ) : outcome.kind === 'version_conflict' ? (
        <T id="ai_os.conflict.changed_elsewhere" />
      ) : outcome.kind === 'idempotency_mismatch' ? (
        <T id="ai_os.conflict.same_key_different_content" />
      ) : outcome.kind === 'red_needs_approval' ? (
        <T id="ai_os.retry.blocked_by_red" />
      ) : (
        <T id="ai_os.error.create_failed" />
      )}
    </p>
  )
}
